/**
 * Authentication Service - REFACTORIZADO (SRP)
 * Responsabilidad: Solo autenticación Firebase
 *
 * ANTES: Mezclaba autenticación con creación de documentos de usuario
 * AHORA: Solo maneja autenticación Firebase
 *
 * La creación de documentos de usuario se movió a userService.ts
 * La orquestación se hace en el store de auth
 */

import { auth } from './config'
import type { FirebaseError } from 'firebase/app'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification as firebaseSendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  type OAuthCredential,
  type User,
} from 'firebase/auth'

/**
 * Código con el que `signInWithPopup` rechaza cuando el correo de la cuenta
 * Google ya tiene una cuenta de OTRO proveedor (contraseña) y Firebase no
 * puede resolverlo solo — correos no alojados en Google. Ver ADR-SEC-017.
 */
export const ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL =
  'auth/account-exists-with-different-credential'

/**
 * Lo que hace falta para terminar una vinculación pendiente: el correo que
 * Google informó (puede faltar) y la credencial de Google que quedó sin usar.
 */
export interface PendingGoogleLink {
  email: string | null
  credential: OAuthCredential
}

/** Código de un error del SDK de Firebase, o `undefined` si no lo tiene. */
export const getAuthErrorCode = (error: unknown): string | undefined => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code
    return typeof code === 'string' ? code : undefined
  }
  return undefined
}

/**
 * Si el error de Google es `account-exists-with-different-credential`, devuelve
 * la vinculación pendiente; si no, `null`.
 *
 * NO consulta `fetchSignInMethodsForEmail`: con la protección contra
 * enumeración de correos activa (los tres proyectos) devuelve vacío. La decisión
 * sale del código de error, y NADA aquí mira `emailVerified` — la política del
 * owner es vincular aunque el correo no esté verificado (ADR-SEC-017).
 * @see specs/API-CONTRACT/firestore-data-access.md §"Proveedores y vinculación de cuentas"
 */
export const getPendingGoogleLink = (error: unknown): PendingGoogleLink | null => {
  if (getAuthErrorCode(error) !== ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL) return null

  const credential = GoogleAuthProvider.credentialFromError(error as FirebaseError)
  if (!credential) return null

  const email = (error as FirebaseError).customData?.email
  return { email: typeof email === 'string' && email !== '' ? email : null, credential }
}

/**
 * Vincula una credencial de Google a la sesión ya iniciada (con contraseña) y
 * recarga el usuario para que la sesión refleje los proveedores y la
 * verificación del correo que Firebase haya actualizado al vincular.
 * Solo autentica: no toca documentos de Firestore (SRP, ADR-SEC-001).
 */
export const linkGoogleCredential = async (
  user: User,
  credential: OAuthCredential
): Promise<User> => {
  const result = await linkWithCredential(user, credential)
  await result.user.reload()
  return result.user
}

/**
 * Sign in with email and password
 * Solo autentica, no crea documentos
 */
export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password)
  return userCredential.user
}

/**
 * Sign in with Google OAuth
 * Solo autentica, no crea documentos
 *
 * NOTA (2026-10-04, ADR-SEC-018): quién crea el documento después depende del
 * flujo — el store lo aprovisiona sólo desde el REGISTRO (casilla de términos
 * marcada); desde el login lo hace el «Continuar» del diálogo «Un último paso».
 */
export const loginWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider()
  provider.addScope('profile')
  provider.addScope('email')

  const userCredential = await signInWithPopup(auth, provider)
  return userCredential.user
}

/**
 * Verificar si un usuario es nuevo (primera vez que inicia sesión)
 * Útil para determinar si se debe crear documento de usuario
 */
export const isNewUser = (user: User): boolean => {
  return user.metadata.creationTime === user.metadata.lastSignInTime
}

/**
 * Register new user with email and password
 * Solo crea usuario en Firebase Auth, no crea documento
 *
 * `displayName` llega **ya derivado** con la regla del ecosistema
 * (`deriveDisplayName` de `src/utils/displayName.ts`): el store lo deriva una sola
 * vez y pasa ESE valor aquí, a `users` y al espejo de `professor_profiles`, para
 * que los tres escritores guarden la misma cadena (ADR-023). Aquí no se re-deriva.
 *
 * NOTA: El store de auth debe llamar a userService.createUserDocument()
 * después de esta función
 */
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string
): Promise<User> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password)
  const user = userCredential.user

  // Solo actualizar perfil
  await updateProfile(user, { displayName })

  return user
}

/**
 * Escribe el nombre en la cuenta de Firebase Auth (copia del nombre cuya autoridad
 * es `users.display_name`). Se llama **después** de escribir Firestore (ADR-023):
 * si falla, el llamador conserva Firestore y decide cómo avisar.
 *
 * `updateProfile` muta en sitio el `User` (`auth.currentUser`) al resolver y no
 * dispara `onAuthStateChanged`: quien muestre el nombre debe refrescarse
 * (`useAuthStore().refreshUser()`).
 */
export const updateUserDisplayName = async (user: User, displayName: string): Promise<void> => {
  await updateProfile(user, { displayName })
}

/**
 * Igual que `updateUserDisplayName` sobre la sesión vigente. Lanza si no hay sesión.
 */
export const updateCurrentUserDisplayName = async (displayName: string): Promise<void> => {
  const current = auth.currentUser
  if (!current) throw new Error('No hay una sesión iniciada')
  await updateProfile(current, { displayName })
}

/**
 * Sign out current user
 */
export const logout = async (): Promise<void> => {
  await signOut(auth)
}

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email)
}

/**
 * Send email verification
 */
export const sendEmailVerification = async (user: User): Promise<void> => {
  await firebaseSendEmailVerification(user)
}

/**
 * Get current user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser
}
